import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router";
import { ROUTES } from "./constants/routes";
import MainLayout from "./core/layout/MainLayout";
import { AuthProvider } from "./features/auth/contexts/AuthContext";
import ProtectedRoute from "./features/auth/components/ProtectedRoute";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import EmailVerificationPage from "./pages/auth/EmailVerificationPage";
import AuthConfirmPage from "./pages/auth/AuthConfirmPage";
import { ErrorProvider } from "./shared/contexts/ErrorContext";
import ErrorBoundary from "./shared/components/errors/ErrorBoundary";
import { Analytics } from "@vercel/analytics/react";
import { ConsentProvider, useConsent } from "./shared/contexts/ConsentContext";
import { CookieConsentBanner } from "./shared/components/banners/CookieConsentBanner";
import { GoogleAnalytics } from "./shared/analytics/GoogleAnalytics";
import { ChatContextProvider } from "./features/chatbot/ChatContext";

// Lazy load heavy components
const DataUploadPage = lazy(() => import("./pages/data-upload/DataUploadPage"));
const FirstProcessingPage = lazy(() => import("./pages/first-processing/FirstProcessingPage"));
const DataProcessingPage = lazy(() => import("./pages/data-processing/DataProcessingPage"));
const DataAdjustmentsPage = lazy(() => import("./pages/data-adjustments/DataAdjustmentsPage"));
const DataCloudPage = lazy(() => import("./pages/data-cloud/DataCloudPage"));
const TrainingPage = lazy(() => import("./pages/training/TrainingPage"));
const ForecastApiPage = lazy(() => import("./pages/forecast-api/ForecastApiPage"));
const CsvFilterPage = lazy(() => import("./pages/csv-filter/CsvFilterPage"));
const PricingPage = lazy(() => import("./pages/pricing/PricingPage"));
const LandingPage = lazy(() => import("./pages/landing/LandingPage"));
const PaymentSuccessPage = lazy(() => import("./pages/payment/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/payment/PaymentCancelPage"));
const ImpressumPage = lazy(() => import("./pages/legal/ImpressumPage"));
const DatenschutzPage = lazy(() => import("./pages/legal/DatenschutzPage"));
const AgbPage = lazy(() => import("./pages/legal/AgbPage"));
// UsageTrackingTest removed - test page not in production

// Loading component
const PageLoader = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    fontSize: '1.2rem',
    color: '#666'
  }}>
    Loading...
  </div>
);

function GatedAnalytics() {
  const { analyticsAllowed } = useConsent();
  return analyticsAllowed ? <Analytics /> : null;
}

function App() {
  return (
    <ErrorBoundary>
      <ErrorProvider>
        <ConsentProvider>
          <GatedAnalytics />
          <AuthProvider>
            <ChatContextProvider>
            <Router>
        <Routes>
          <Route
            path={ROUTES.LANDING}
            element={
              <Suspense fallback={<PageLoader />}>
                <LandingPage locale="en" />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.LANDING_DE}
            element={
              <Suspense fallback={<PageLoader />}>
                <LandingPage locale="de" />
              </Suspense>
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<EmailVerificationPage />} />
          <Route path="/auth/confirm" element={<AuthConfirmPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path={ROUTES.STANDARDISIERUNG}
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<PageLoader />}>
                    <DataUploadPage />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ZEITRASTEREINSTELLUNG}
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<PageLoader />}>
                    <FirstProcessingPage />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ANOMALIEERKENNUNG}
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<PageLoader />}>
                    <DataProcessingPage />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DATA_ADJUSTMENTS}
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<PageLoader />}>
                    <DataAdjustmentsPage />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DATENWOLKE}
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<PageLoader />}>
                    <DataCloudPage />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/training"
            element={
              <ProtectedRoute requiredFeature="training">
                <MainLayout>
                  <Suspense fallback={<PageLoader />}>
                    <TrainingPage />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/forecast-api"
            element={
              <ProtectedRoute requiredFeature="training">
                <MainLayout>
                  <Suspense fallback={<PageLoader />}>
                    <ForecastApiPage />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <MainLayout>
                <Suspense fallback={<PageLoader />}>
                  <PricingPage />
                </Suspense>
              </MainLayout>
            }
          />
          <Route
            path="/payment/success"
            element={
              <ProtectedRoute requirePaidSubscription={false}>
                <Suspense fallback={<PageLoader />}>
                  <PaymentSuccessPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment/cancel"
            element={
              <ProtectedRoute requirePaidSubscription={false}>
                <Suspense fallback={<PageLoader />}>
                  <PaymentCancelPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CSV_FILTER}
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<PageLoader />}>
                    <CsvFilterPage />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/impressum"
            element={
              <Suspense fallback={<PageLoader />}>
                <ImpressumPage />
              </Suspense>
            }
          />
          <Route
            path="/datenschutz"
            element={
              <Suspense fallback={<PageLoader />}>
                <DatenschutzPage />
              </Suspense>
            }
          />
          <Route
            path="/agb"
            element={
              <Suspense fallback={<PageLoader />}>
                <AgbPage />
              </Suspense>
            }
          />
          {/* Redirects from legacy German slugs to the new English slugs */}
          <Route path="/rohdaten-laden" element={<Navigate to={ROUTES.STANDARDISIERUNG} replace />} />
          <Route path="/erste-bearbeitung" element={<Navigate to={ROUTES.ZEITRASTEREINSTELLUNG} replace />} />
          <Route path="/zweite-bearbeitung" element={<Navigate to={ROUTES.ANOMALIEERKENNUNG} replace />} />
          <Route path="/dritte-bearbeitung" element={<Navigate to={ROUTES.DATA_ADJUSTMENTS} replace />} />
          <Route path="/vierte-bearbeitung" element={<Navigate to={ROUTES.DATENWOLKE} replace />} />
          <Route path="/csv-filter" element={<Navigate to={ROUTES.CSV_FILTER} replace />} />
          {/* Unknown URLs render a blank page at HTTP 200 otherwise, which
              Google indexes as soft 404s. Locale prefixes make /de/anything a
              likelier guess, so it is handled here. */}
          <Route path="*" element={<Navigate to={ROUTES.LANDING} replace />} />
          </Routes>
          <GoogleAnalytics />
          <CookieConsentBanner />
          </Router>
            </ChatContextProvider>
          </AuthProvider>
        </ConsentProvider>
      </ErrorProvider>
    </ErrorBoundary>
  );
}

export default App;
